var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
/* eslint-disable no-console */
import React, { useImperativeHandle, useEffect, forwardRef, useContext, useRef, } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useConfigContext } from '@m4b-design/config-provider';
import merge from 'lodash/merge';
import cs from 'classnames';
import { ConfigProvider as ArcoConfigProvider, Grid } from '@arco-design/web-react';
import { FormContext as RawFormContext, FormProviderContext, } from './context';
import { isObject, omit, ID_SUFFIX } from './utils';
import useMergeProps from './hooks/useMergeProps';
import useForm from './useForm';
var ConfigContext = ArcoConfigProvider.ConfigContext;
function getFormElementId(prefix, field) {
    var id = field === null || field === void 0 ? void 0 : field.replace(/[\[\.]/g, '_').replace(/\]/g, '');
    return prefix ? "".concat(prefix, "-").concat(id) : "".concat(id);
}
var defaultProps = {
    layout: 'vertical',
    labelCol: { span: 7, offset: 0 },
    labelAlign: 'right',
    wrapperCol: { span: 17, offset: 0 },
    wrapper: 'form',
    validateTrigger: 'onChange',
    requiredSymbol: false,
    closeColumns: false,
    columnsNum: 1,
    columnsGutter: 16,
};
// 类型屏蔽layout属性， 布局只支持 vertical
// 属性新增分栏能力 columnsNum, 默认为1 最大为 4
// const layout = 'vertical';
var Form = function (baseProps, ref) {
    var _a;
    var _b, _c;
    var globalPrefixCls = useConfigContext().prefixCls;
    var prefixClsForm = "".concat(globalPrefixCls, "-form");
    //  获取form 外部的 arco config provider  问题： 我需要在pro-form 额外再接收arco的 config provider 吗
    var ctx = useContext(ConfigContext);
    // Form.Provider 组件管理多个表单的数据
    var formProviderCtx = useContext(FormProviderContext);
    // 控制整体表单的一个盒子
    var wrapperRef = useRef(null);
    // 表单实例， 可以操作表单的一些行为，从而进行表单的数据处理
    var _d = __read(useForm(baseProps.form), 1), formInstance = _d[0];
    // 表单是否已经挂载，在第一次 useEffect 之后就会赋值 为true
    var isMount = useRef();
    // 传入的参数 baseProps，  默认参数 defaultProps， 全局配置的所有组件的默认参数 ctx.componentConfig
    var props = useMergeProps(baseProps, defaultProps, (_b = ctx.componentConfig) === null || _b === void 0 ? void 0 : _b.Form);
    var layout = props.layout, requiredSymbol = props.requiredSymbol, validateMessagesDisplayType = props.validateMessagesDisplayType, // 表单校验信息的展示形式
    labelCol = props.labelCol, // 默认 {span: 5, offset: 0}
    wrapperCol = props.wrapperCol, // 默认 {span: 19, offset: 0}
    Wrapper = props.wrapper, // 默认 form 可以自定义 容器tag 比如用 div 或者 p
    id = props.id, // 设置后，会作为表单控件 id的前缀, 默认undefined
    labelAlign = props.labelAlign, // label 的对齐方式， 默认 right
    disabled = props.disabled, // 全局控制为disabled 权重比 form-item 低 默认undefined
    colon = props.colon, // 全局控制为label 是否显示冒号的 权重比 form-item 低, 默认undefined
    className = props.className, // 类名 默认undefined
    validateTrigger = props.validateTrigger, // 默认Onchange
    formPrefixCls = props.prefixCls, // 样式的scope 默认为 arco
    validateMessages = props.validateMessages, // 默认undefined
    /**
     * 处理form 表单的多列问题，目前默认是单列。
     * closeColumns = faslse, columnsNum = 1 , columnsGutter = 16
     * 带来的效果是 form 的每一项独占一行，每一行的间距是 24
     * 当 closeColumns = fasle, columnsNum 不等于 1 的时候， 列间距由columnsGutter确定；
     */
    _e = props.closeColumns, // 默认undefined
    /**
     * 处理form 表单的多列问题，目前默认是单列。
     * closeColumns = faslse, columnsNum = 1 , columnsGutter = 16
     * 带来的效果是 form 的每一项独占一行，每一行的间距是 24
     * 当 closeColumns = fasle, columnsNum 不等于 1 的时候， 列间距由columnsGutter确定；
     */
    closeColumns = _e === void 0 ? false : _e, _f = props.columnsNum, columnsNum = _f === void 0 ? 1 : _f, _g = props.columnsGutter, columnsGutter = _g === void 0 ? 16 : _g, optional = props.optional, // 是否需要将form表单设置为全局非必填模式
    itemSpacingSize = props.itemSpacingSize, // form-item 的间距
    maintainRawRules = props.maintainRawRules, _h = props.wrapperRowProps, wrapperRowProps = _h === void 0 ? {} : _h, rest = __rest(props, ["layout", "requiredSymbol", "validateMessagesDisplayType", "labelCol", "wrapperCol", "wrapper", "id", "labelAlign", "disabled", "colon", "className", "validateTrigger", "prefixCls", "validateMessages", "closeColumns", "columnsNum", "columnsGutter", "optional", "itemSpacingSize", "maintainRawRules", "wrapperRowProps"]);
    // 是否为垂直布局
    var isVertical = layout === 'vertical';
    // 获取 自定义 scope , 如果没有 则会 arco-form
    var prefixCls = formPrefixCls || ctx.getPrefixCls('form');
    /**
     * @zh 视图的表现形式是从右开始向左结束。 可以理解如果为true , 则视图展示就跟原有视图成中心轴镜像关系
     * @en View starts from the right and ends on the left.
     * @type boolean
     */
    var rtl = ctx.rtl;
    // 获取 size 值，默认 default
    var size = 'size' in props ? props.size : ctx.size;
    // 获取表单内部调用的一些api 名字
    var innerMethods = formInstance.getInnerMethods(true);
    // 表单如果还没有挂载， 记录当前的初始值，在store里将对应变量根据这个值来初始化一遍
    if (!isMount.current) {
        innerMethods.innerSetInitialValues(props.initialValues);
    }
    // 表单挂载时触发，只一次
    useEffect(function () {
        isMount.current = true;
    }, []);
    // 根据form id 注册form 信息 formsRef.current[id] = formInstance 并返回销毁函数
    useEffect(function () {
        var unregister;
        if (formProviderCtx.register) {
            unregister = formProviderCtx.register(props.id, formInstance);
        }
        return unregister;
    }, [props.id, formInstance]);
    // 允许外部直接通过 form 的 ref 获取form内的 formInstance 并进行操作
    useImperativeHandle(ref, function () {
        return formInstance;
    });
    // 滚动到目标表单字段位置
    formInstance.scrollToField = function (field, options) {
        var node = wrapperRef.current;
        var id = props.id;
        if (!node) {
            return;
        }
        var fieldNode = node.querySelector("#".concat(getFormElementId(id, field)));
        if (!fieldNode) {
            // 如果设置了nostyle， fieldNode不存在，尝试直接查询表单控件
            fieldNode = node.querySelector("#".concat(getFormElementId(id, field)).concat(ID_SUFFIX));
        }
        // 滚动到对应的field
        fieldNode &&
            scrollIntoView(fieldNode, __assign({ behavior: 'smooth', block: 'nearest', scrollMode: 'if-needed' }, options));
    };
    // 设置内置方式调用，并能让方式在多表单场景触发。
    innerMethods.innerSetCallbacks({
        onValuesChange: function (value, values) {
            props.onValuesChange && props.onValuesChange(value, values);
            formProviderCtx.onFormValuesChange && formProviderCtx.onFormValuesChange(props.id, value);
        },
        onChange: props.onChange,
        onValidateFail: function (errors) {
            if (props.scrollToFirstError) {
                var options = isObject(props.scrollToFirstError) ? props.scrollToFirstError : {};
                formInstance.scrollToField(Object.keys(errors)[0], options);
            }
        },
        onSubmitFailed: props.onSubmitFailed,
        onSubmit: function (values) {
            var returnValue = props.onSubmit && props.onSubmit(values);
            formProviderCtx.onFormSubmit && formProviderCtx.onFormSubmit(props.id, values);
            return returnValue;
        },
    });
    // 管理多级表单时，需要同步过去的一些表单全局属性
    var contextProps = {
        layout: layout,
        requiredSymbol: requiredSymbol,
        maintainRawRules: maintainRawRules,
        itemSpacingSize: itemSpacingSize,
        optional: optional,
        closeColumns: closeColumns,
        columnsNum: columnsNum,
        columnsGutter: columnsGutter,
        validateMessagesDisplayType: validateMessagesDisplayType,
        labelAlign: labelAlign,
        disabled: disabled,
        colon: colon,
        labelCol: labelCol,
        wrapperCol: wrapperCol,
        store: formInstance,
        prefixCls: prefixCls,
        validateTrigger: validateTrigger,
        formItemAlone: false,
        validateMessages: merge({}, (_c = ctx.locale.Form) === null || _c === void 0 ? void 0 : _c.validateMessages, validateMessages),
        getFormElementId: function (field) { return getFormElementId(id, field); },
    };
    var FormContext = RawFormContext;
    return (
    // 将外部的 ConfigProvider 的值处理一遍，增加size 覆盖外部的 ConfigProvider 供form-item消费
    React.createElement(ArcoConfigProvider, __assign({}, ctx, { size: size }),
        React.createElement(FormContext.Provider, { value: contextProps },
            React.createElement(Wrapper, __assign({ ref: wrapperRef, "data-tid": "m4b_form" }, omit(rest, [
                'prefixCls',
                'id',
                'layout',
                'labelCol',
                'wrapperCol',
                'labelAlign',
                'validateTrigger',
                'wrapper',
                'colon',
                'validateMessages',
                'disabled',
                'form',
                'size',
                'initialValues',
                'onValuesChange',
                'onChange',
                'wrapperProps',
                'scrollToFirstError',
                'onSubmit',
                'onSubmitFailed',
                'columnsNum',
                'closeColumns',
                'columnsGutter',
                'validateMessagesDisplayType',
            ]), props.wrapperProps, { className: cs(prefixCls, prefixClsForm, "".concat(prefixCls, "-").concat(layout), "".concat(prefixCls, "-size-").concat(size), (_a = {}, _a["".concat(prefixCls, "-rtl")] = rtl, _a), className), style: props.style, onSubmit: function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    formInstance.submit();
                }, id: id }), isVertical && !closeColumns ? (React.createElement(Grid.Row, __assign({ gutter: columnsNum === 1 ? 0 : columnsGutter }, wrapperRowProps), props.children)) : (props.children)))));
};
var FormComponent = forwardRef(Form);
FormComponent.displayName = 'Form';
export default FormComponent;
